<template>
  <CollectionCompanyView
    :companies="companies"
    :loading="loading"
    :error="error"
    :show-modal="showModal"
    :show-delete-modal="showDeleteModal"
    :is-edit-mode="isEditMode"
    :form-data="formData"
    :form-errors="formErrors"
    :delete-target="deleteTarget"
    @show-add-modal="showAddModal"
    @show-edit-modal="showEditModal"
    @confirm-delete="confirmDelete"
    @add-company="handleAddCompany"
    @edit-company="handleEditCompany"
    @delete-company="handleDeleteCompany"
    @close-modal="closeModal"
    @close-delete-modal="closeDeleteModal"
    @back-to-menu="handleBackToMenu"
    @table-action="handleTableAction"
    @submit="handleSubmit"
    @update:form-data="updateFormData"
    :company-order="companyOrder"
  />
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import CollectionCompanyView from '../components/collectionCompany/CollectionCompanyView.vue'
import * as collectionCompanyService from '../../../services/systemSettings/collectionCompanyService.js'
import { calculateCompanyColor } from '../../../services/systemSettings/collectionCompanyService.js'

const router = useRouter()

const companies = ref([])
const loading = ref(false)
const error = ref(null)

const showModal = ref(false)
const showDeleteModal = ref(false)
const isEditMode = ref(false)

const formData = ref({
  companyName: ''
})
const formErrors = ref({})
const deleteTarget = ref(null)

// 企業の順番を計算するcomputed
const companyOrder = computed(() => {
  return isEditMode.value
    ? companies.value.findIndex(c => c.id === formData.value.id) + 1
    : companies.value.length + 1
})

const resetForm = () => {
  formData.value = {
    companyName: ''
  }
  formErrors.value = {}
}

const updateFormData = (val) => {
  formData.value = val
}

const showAddModal = () => {
  isEditMode.value = false
  resetForm()
  showModal.value = true
}

const showEditModal = (company) => {
  isEditMode.value = true
  formData.value = { ...company }
  formErrors.value = {}
  showModal.value = true
}

const confirmDelete = (company) => {
  deleteTarget.value = company
  showDeleteModal.value = true
}

const closeModal = () => {
  resetForm()
  showModal.value = false
}

const closeDeleteModal = () => {
  deleteTarget.value = null
  showDeleteModal.value = false
}

/**
 * テーブルアクションの統合ハンドラー
 */
const handleTableAction = (payload) => {
  const { action, data } = payload
  switch (action) {
    case 'edit':
      showEditModal(data)
      break
    case 'delete':
      confirmDelete(data)
      break
    default:
      console.warn('Unknown table action:', action)
  }
}

/**
 * フォーム送信処理（バリデーション＋色計算をContainerで実施）
 */
const handleSubmit = () => {
  const errors = {}

  if (!formData.value.companyName?.trim()) {
    errors.companyName = '企業名は必須です'
  }

  const existingCompany = companies.value.find(company =>
    company.companyName === formData.value.companyName?.trim() &&
    (!isEditMode.value || company.id !== formData.value.id)
  )
  if (existingCompany) {
    errors.companyName = 'この企業名は既に登録されています'
  }

  if (Object.keys(errors).length > 0) {
    formErrors.value = errors
    return
  }

  const order = companyOrder.value
  const colorInfo = calculateCompanyColor(order)
  const companyData = {
    ...formData.value,
    companyName: formData.value.companyName?.trim(),
    companyColor: colorInfo.color,
    companyColorName: colorInfo.name
  }

  if (isEditMode.value) {
    handleEditCompany(companyData)
  } else {
    handleAddCompany(companyData)
  }
}

const handleAddCompany = async (companyData) => {
  loading.value = true
  error.value = null

  try {
    const result = await collectionCompanyService.createCollectionCompany(companyData)

    if (result.success) {
      companies.value = result.data
      closeModal()
    } else {
      error.value = result.message
    }

  } catch (err) {
    error.value = '企業の追加に失敗しました'
    console.error('Add company error:', err)
  } finally {
    loading.value = false
  }
}

const handleEditCompany = async (companyData) => {
  loading.value = true
  error.value = null

  try {
    const result = await collectionCompanyService.updateCollectionCompany(companyData.id, companyData)

    if (result.success) {
      companies.value = result.data
      closeModal()
    } else {
      error.value = result.message
    }

  } catch (err) {
    error.value = '企業の更新に失敗しました'
    console.error('Edit company error:', err)
  } finally {
    loading.value = false
  }
}

const handleDeleteCompany = async (companyData) => {
  loading.value = true
  error.value = null

  try {
    const result = await collectionCompanyService.deleteCollectionCompany(companyData.id)

    if (result.success) {
      companies.value = result.data
      closeDeleteModal()
    } else {
      error.value = result.message
    }

  } catch (err) {
    error.value = '企業の削除に失敗しました'
    console.error('Delete company error:', err)
  } finally {
    loading.value = false
  }
}

const handleBackToMenu = () => {
  router.push('/system-settings')
}

const loadCompanies = async () => {
  loading.value = true
  error.value = null

  try {
    const result = await collectionCompanyService.getCollectionCompanies()

    if (result.success) {
      // 色情報をデータに付与
      companies.value = (result.data || []).map((company, index) => {
        const colorInfo = calculateCompanyColor(index + 1)
        return {
          ...company,
          companyColor: colorInfo.color,
          companyColorName: colorInfo.name
        }
      })
    } else {
      error.value = result.message || '企業一覧の取得に失敗しました'
    }

  } catch (err) {
    error.value = '企業一覧の取得中にエラーが発生しました'
    console.error('Load companies error:', err)
  } finally {
    loading.value = false
  }
}

// コンポーネント作成時に企業一覧を取得
onMounted(async () => {
  await loadCompanies()
})
</script>

<style scoped>
/* このコンテナコンポーネントではスタイルは不要 */
/* すべてのスタイルはViewコンポーネントで管理 */
</style>
